import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocItemsTable,
  DocDphSelect,
  type DocItem,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocChain,
  useDocTransferIn,
  DocChainBar,
  DocTransferButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  ContactPickerButton,
  type AresPickResult,
  type CompanyPickResult,
  type ContactPerson,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { printToPdf } from '../../lib/printToPdf';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Cenová nabídka — „živý dokument".
 * Sekce: Údaje nabídky / Stavba / Dodavatel / Odběratel /
 * Položky nabídky / Platební podmínky / Termíny / Podpisy.
 */

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && /[\p{L}]/u.test(w[0]) && !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w));
  if (words.length === 0) return '—';
  return words
    .slice(0, 3)
    .map((w) => w[0].toLocaleUpperCase('cs'))
    .join('');
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function NabidkaPage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [cislo, setCislo] = useState('');
  const [datum, setDatum] = useState('');
  const [platnost, setPlatnost] = useState('');
  const [zakazka, setZakazka] = useState('');
  const [zahajeni, setZahajeni] = useState('');
  const [dokonceni, setDokonceni] = useState('');

  const [stavba, setStavba] = useState({
    nazev: '',
    iniciály: '',
    adresa: '',
    cPovoleni: '',
  });
  const [dod, setDod] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    kontakt: '',
    tel: '',
    email: '',
  });
  const [obj, setObj] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    kontakt: '',
    tel: '',
    email: '',
  });
  const [poznamka, setPoznamka] = useState('');
  const [items, setItems] = useState<DocItem[]>([
    { id: '1', desc: '', qty: '', unit: '', price: '', total: '' },
  ]);
  const [dphPct, setDphPct] = useState('21');

  const [podminky, setPodminky] = useState({
    zaloha: '',
    prubezne: '',
    doplatek: '',
  });

  const [podpisy, setPodpisy] = useState({
    zpracoval: { jmeno: '', funkce: '', image: '' },
    schvalil: { jmeno: '', funkce: '', image: '' },
    extra: [] as Array<{ id: string; strana: string; jmeno: string; funkce: string; image: string }>,
  });

  // Skryté řádky — uživatel může jednotlivé řádky odebrat ✕,
  // skupinové „obnovit" je pak v hlavičce sekce. Plus vlastní řádky „+ Řádek".
  const chain = useDocChain();
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const calc = useMemo(() => {
    const parseNum = (s: string) => {
      const cleaned = (s ?? '').replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, '');
      const n = Number(cleaned);
      return isFinite(n) ? n : 0;
    };
    const sub = items.reduce((a, it) => a + parseNum(it.total), 0);
    const rate = (parseFloat(dphPct) || 0) / 100;
    const dph = Math.round(sub * rate);
    const total = sub + dph;
    return { sub, dph, total };
  }, [items, dphPct]);

  const fmtKc = (n: number) =>
    n.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/,/g, ' ') + ' Kč';

  const docTitle = cislo ? `Cenová nabídka ${cislo}` : 'Cenová nabídka';
  const metaLine = [cislo && `č. ${cislo}`, platnost && `platnost do ${platnost}`]
    .filter(Boolean)
    .join(' · ');

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.cislo != null) setCislo(String(d.cislo));
    if (d.datum != null) setDatum(String(d.datum));
    if (d.platnost != null) setPlatnost(String(d.platnost));
    if (d.zakazka != null) setZakazka(String(d.zakazka));
    if (d.zahajeni != null) setZahajeni(String(d.zahajeni));
    if (d.dokonceni != null) setDokonceni(String(d.dokonceni));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (d.dod) setDod((x) => ({ ...x, ...(d.dod as Partial<typeof dod>) }));
    if (d.obj) setObj((x) => ({ ...x, ...(d.obj as Partial<typeof obj>) }));
    if (Array.isArray(d.items)) setItems(d.items as DocItem[]);
    if (d.dphPct != null) setDphPct(String(d.dphPct));
    if (d.podminky) setPodminky((p) => ({ ...p, ...(d.podminky as Partial<typeof podminky>) }));
    if (d.poznamka != null) setPoznamka(String(d.poznamka));
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    chain.apply(d);
    rows.apply(d.radky);
  }

  const docData = {
    cislo, datum, platnost, zakazka, zahajeni, dokonceni,
    stavba, dod, obj, items, dphPct, podminky, poznamka, podpisy,
    radky: rows.snapshot(),
    zdroj: chain.zdroj,
  };
  const { editId } = useDocEditor(applySnapshot);
  useDocTransferIn('nabidka', applySnapshot, editId);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového dokumentu) —
  // bez něj se při zavření/reloadu záložky ztratil celý rozepsaný formulář.
  const draft = useDocDraft<Record<string, unknown>>('nabidka', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cislo, datum, platnost, zakazka, zahajeni, dokonceni, stavba, dod, obj, items, dphPct, podminky, poznamka, podpisy, rows, chain.zdroj]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření a uživatel si myslel, že je dokument uložený.
   */
  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? chain.savedId ?? undefined,
        doc_type: 'nabidka',
        title: docTitle,
        stavba: activeStavba?.nazev ?? '',
        data_json: docData,
      });
      draft.clear();
      toast.success('Cenová nabídka uložena do archivu.');
      chain.markSaved(res.id);
      return res.id;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      return null;
    }
  }

  const setDodPatch = (patch: Partial<typeof dod>) => setDod((d) => ({ ...d, ...patch }));
  const setObjPatch = (patch: Partial<typeof obj>) => setObj((o) => ({ ...o, ...patch }));
  const setStavbaPatch = (patch: Partial<typeof stavba>) => setStavba((s) => ({ ...s, ...patch }));

  const addExtra = () =>
    setPodpisy((p) => ({
      ...p,
      extra: [
        ...p.extra,
        { id: newId('ex'), strana: 'Účastník', jmeno: '', funkce: '', image: '' },
      ],
    }));
  const updateExtra = (id: string, patch: Partial<{ strana: string; jmeno: string; funkce: string; image: string }>) =>
    setPodpisy((p) => ({
      ...p,
      extra: p.extra.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  const removeExtra = (id: string) =>
    setPodpisy((p) => ({ ...p, extra: p.extra.filter((x) => x.id !== id) }));

  return (
    <>
      <DocLayout
        breadcrumb={['Dokumenty', 'Cenová nabídka']}
        title={docTitle}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="nabidka" path="/app/nabidka" />
            <SavedDocsButton
              docType="nabidka"
              title="Uložené cenové nabídky"
              currentId={editId}
            />
            <Button variant="secondary" leading="⎙" onClick={() => printToPdf(`Cenova_nabidka_${cislo || 'koncept'}.pdf`)}>PDF</Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <DocTransferButton
              docType="nabidka"
              getData={() => docData}
              save={handleSave}
              title={docTitle}
              zdroj={chain.zdroj}
            />
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <DocChainBar zdroj={chain.zdroj} />
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(dod.nazev),
              name: dod.nazev || '—',
              address: `${dod.sidlo || '—'}${dod.ico ? ' · IČ ' + dod.ico : ''}`,
            }}
            tag="Cenová nabídka"
            title="Cenová nabídka na stavební práce"
            meta={metaLine || undefined}
            footer={{ left: dod.nazev || '—', center: 'str. 1 / 1', right: cislo || '—' }}
          >
            <DocBlock
              idx="01"
              title="Údaje nabídky"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={<DocAddRowButton rows={rows} section="s01" />}
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo nabídky"
                  mono
                  value={cislo}
                  onChange={setCislo}
                  placeholder="např. CN-2026/12"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.datum') && (
                <DocRow
                  k="Datum vystavení"
                  mono
                  value={datum}
                  onChange={setDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={datum} onChange={setDatum} />}
                  onRemove={() => hide('s01.datum')}
                />
              )}
              {!isHidden('s01.platnost') && (
                <DocRow
                  k="Platnost do"
                  mono
                  value={platnost}
                  onChange={setPlatnost}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={platnost} onChange={setPlatnost} />}
                  onRemove={() => hide('s01.platnost')}
                />
              )}
              {!isHidden('s01.zakazka') && (
                <DocRow
                  k="Číslo zakázky"
                  mono
                  value={zakazka}
                  onChange={setZakazka}
                  placeholder="např. Z-2026/0412"
                  onRemove={() => hide('s01.zakazka')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Stavba"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) =>
                      setStavba((s) => ({
                        ...s,
                        nazev: p.nazev,
                        iniciály: deriveInitials(p.nazev),
                        adresa: p.adresa,
                        cPovoleni: p.stavebni_povoleni,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: stavba.nazev,
                      adresa: stavba.adresa,
                      stavebni_povoleni: stavba.cPovoleni,
                      zakazka_cislo: zakazka,
                      datum_zahajeni: zahajeni,
                      datum_ukonceni: dokonceni,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.nazev') && (
                <DocRow
                  k="Název stavby"
                  value={stavba.nazev}
                  placeholder="např. Novostavba RD Březník — etapa II"
                  onChange={(v) => {
                    const auto = deriveInitials(stavba.nazev);
                    setStavba((s) => ({
                      ...s,
                      nazev: v,
                      iniciály: !s.iniciály || s.iniciály === auto ? deriveInitials(v) : s.iniciály,
                    }));
                  }}
                  onRemove={() => hide('s02.nazev')}
                />
              )}
              {!isHidden('s02.adresa') && (
                <DocRow
                  k="Adresa plnění"
                  value={stavba.adresa}
                  onChange={(v) => setStavbaPatch({ adresa: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={stavba.adresa}
                      onApply={(v) => setStavbaPatch({ adresa: v })}
                      title="Adresa plnění"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.adresa')}
                />
              )}
              {!isHidden('s02.povoleni') && (
                <DocRow
                  k="Číslo stavebního povolení"
                  value={stavba.cPovoleni}
                  onChange={(v) => setStavbaPatch({ cPovoleni: v })}
                  placeholder="např. MÚ-2026/0341"
                  onRemove={() => hide('s02.povoleni')}
                />
              )}
              {!isHidden('s02.zahajeni') && (
                <DocRow
                  k="Termín zahájení"
                  mono
                  value={zahajeni}
                  onChange={setZahajeni}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={zahajeni} onChange={setZahajeni} />}
                  onRemove={() => hide('s02.zahajeni')}
                />
              )}
              {!isHidden('s02.dokonceni') && (
                <DocRow
                  k="Termín dokončení"
                  mono
                  value={dokonceni}
                  onChange={setDokonceni}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={dokonceni} onChange={setDokonceni} />}
                  onRemove={() => hide('s02.dokonceni')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Dodavatel"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst dodavatele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setDod({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        kontakt: c.kontakt_jmeno,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit dodavatele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: dod.nazev,
                      ico: dod.ico,
                      dic: dod.dic,
                      adresa: dod.sidlo,
                      kontakt_jmeno: dod.kontakt,
                      kontakt_tel: dod.tel,
                      kontakt_email: dod.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={dod.nazev}
                      placeholder="např. Stavby Novák s.r.o."
                      onChange={(v) => setDodPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setDodPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.nazev')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={dod.sidlo}
                  onChange={(v) => setDodPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={dod.sidlo}
                      onApply={(v) => setDodPatch({ sidlo: v })}
                      title="Sídlo dodavatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.kontakt') && (
                <DocRow
                  k="Kontaktní osoba"
                  value={dod.kontakt}
                  onChange={(v) => setDodPatch({ kontakt: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={dod.nazev}
                      companyDraft={{ role: 'zhotovitel', ico: dod.ico, adresa: dod.sidlo }}
                      draftNew={{ jmeno: dod.kontakt, tel: dod.tel, email: dod.email }}
                      title="Kontaktní osoba dodavatele"
                      onPick={(p: ContactPerson) =>
                        setDodPatch({
                          kontakt: p.jmeno,
                          ...(p.tel ? { tel: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s03.kontakt')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={dod.ico}
                  onChange={(v) => setDodPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={dod.ico}
                      onResult={(d) => setDodPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={dod.dic}
                  onChange={(v) => setDodPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={dod.tel}
                  onChange={(v) => setDodPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={dod.email}
                  onChange={(v) => setDodPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={dod.nazev}
                      role="Dodavatel"
                      phone={dod.tel}
                      email={dod.email}
                      title="QR vizitka — dodavatel"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Odběratel"
              hiddenCount={hiddenCount('s04')}
              onRestoreHidden={() => restoreSection('s04')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s04" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst odběratele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setObj({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        kontakt: c.kontakt_jmeno,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit odběratele do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: obj.nazev,
                      ico: obj.ico,
                      dic: obj.dic,
                      adresa: obj.sidlo,
                      kontakt_jmeno: obj.kontakt,
                      kontakt_tel: obj.tel,
                      kontakt_email: obj.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s04.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={obj.nazev}
                      placeholder="např. Petr Svoboda"
                      onChange={(v) => setObjPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s04.nazev')}
                />
              )}
              {!isHidden('s04.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={obj.sidlo}
                  onChange={(v) => setObjPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={obj.sidlo}
                      onApply={(v) => setObjPatch({ sidlo: v })}
                      title="Sídlo odběratele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s04.sidlo')}
                />
              )}
              {!isHidden('s04.kontakt') && (
                <DocRow
                  k="Kontaktní osoba"
                  value={obj.kontakt}
                  onChange={(v) => setObjPatch({ kontakt: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={obj.nazev}
                      companyDraft={{ role: 'objednatel', ico: obj.ico, adresa: obj.sidlo }}
                      draftNew={{ jmeno: obj.kontakt, tel: obj.tel, email: obj.email }}
                      title="Kontaktní osoba objednatele"
                      onPick={(p: ContactPerson) =>
                        setObjPatch({
                          kontakt: p.jmeno,
                          ...(p.tel ? { tel: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s04.kontakt')}
                />
              )}
              {!isHidden('s04.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={obj.ico}
                  onChange={(v) => setObjPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={obj.ico}
                      onResult={(d) => setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s04.ico')}
                />
              )}
              {!isHidden('s04.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={obj.dic}
                  onChange={(v) => setObjPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s04.dic')}
                />
              )}
              {!isHidden('s04.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={obj.tel}
                  onChange={(v) => setObjPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s04.tel')}
                />
              )}
              {!isHidden('s04.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={obj.email}
                  onChange={(v) => setObjPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={obj.nazev}
                      role="Odběratel"
                      phone={obj.tel}
                      email={obj.email}
                      title="QR vizitka — odběratel"
                    />
                  }
                  onRemove={() => hide('s04.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s04" />
            </DocBlock>

            <DocBlock
              idx="05"
              title="Položky nabídky"
              actions={
                <DocDphSelect value={dphPct} onChange={setDphPct} />
              }
            >
              <DocItemsTable items={items} onChange={setItems} title="Položky" />
              <DocRow
                k="Mezisoučet"
                mono
                v={<span style={{ fontFamily: 'var(--sd-font-mono)' }}>{fmtKc(calc.sub)}</span>}
              />
              <DocRow
                k={`DPH ${dphPct} %`}
                mono
                v={<span style={{ fontFamily: 'var(--sd-font-mono)' }}>{fmtKc(calc.dph)}</span>}
              />
              <DocRow
                k="CELKEM"
                mono
                v={
                  <strong style={{ fontFamily: 'var(--sd-font-mono)' }}>
                    {fmtKc(calc.total)}
                  </strong>
                }
              />
            </DocBlock>

            <DocBlock
              idx="06"
              title="Platební podmínky"
              hiddenCount={hiddenCount('s06')}
              onRestoreHidden={() => restoreSection('s06')}
              actions={<DocAddRowButton rows={rows} section="s06" />}
            >
              {!isHidden('s06.zaloha') && (
                <DocRow
                  k="Záloha"
                  value={podminky.zaloha}
                  onChange={(v) => setPodminky((p) => ({ ...p, zaloha: v }))}
                  placeholder="např. 30 % po podpisu smlouvy"
                  onRemove={() => hide('s06.zaloha')}
                />
              )}
              {!isHidden('s06.prubezne') && (
                <DocRow
                  k="Průběžně"
                  value={podminky.prubezne}
                  onChange={(v) => setPodminky((p) => ({ ...p, prubezne: v }))}
                  placeholder="např. dle zjišťovacích protokolů 1× měsíčně"
                  onRemove={() => hide('s06.prubezne')}
                />
              )}
              {!isHidden('s06.doplatek') && (
                <DocRow
                  k="Doplatek"
                  value={podminky.doplatek}
                  onChange={(v) => setPodminky((p) => ({ ...p, doplatek: v }))}
                  placeholder="např. 5 % do 14 dnů od předání"
                  onRemove={() => hide('s06.doplatek')}
                />
              )}
              <DocCustomRows rows={rows} section="s06" />
            </DocBlock>

            <DocBlock idx="07" title="Poznámka">
              <DocRow
                k="Poznámka"
                multiline
                value={poznamka}
                onChange={setPoznamka}
                placeholder="volitelná poznámka k nabídce…"
              />
              <DocRow
                k="Doložka DPH"
                v={
                  <span style={{ fontSize: 11.5, color: 'var(--sd-doc-dim)' }}>
                    Sazba DPH bude dopočítána při fakturaci dle platných zákonů. V případě, že
                    plnění podléhá daňovému režimu dle §92e zákona č. 235/2004 Sb. o DPH, uplatní
                    se přenesená daňová povinnost.
                  </span>
                }
              />
            </DocBlock>

            <DocBlock
              idx="08"
              title="Podpisy"
              actions={
                <DocActionButton onClick={addExtra} title="Přidat dalšího účastníka">
                  + Účastník
                </DocActionButton>
              }
            >
              <DocRow
                k="Zpracoval — jméno"
                value={podpisy.zpracoval.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, zpracoval: { ...p.zpracoval, jmeno: v } }))
                }
              />
              <DocRow
                k="Zpracoval — funkce"
                value={podpisy.zpracoval.funkce}
                placeholder="např. stavbyvedoucí"
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, zpracoval: { ...p.zpracoval, funkce: v } }))
                }
              />
              <DocRow
                k="Schválil — jméno"
                value={podpisy.schvalil.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, schvalil: { ...p.schvalil, jmeno: v } }))
                }
              />
              <DocRow
                k="Schválil — funkce"
                value={podpisy.schvalil.funkce}
                placeholder="např. jednatel"
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, schvalil: { ...p.schvalil, funkce: v } }))
                }
              />
              {podpisy.extra.map((e, i) => (
                <Fragment key={e.id}>
                  <DocRow
                    k={`Účastník ${i + 1} — strana`}
                    value={e.strana}
                    onChange={(v) => updateExtra(e.id, { strana: v })}
                    actions={
                      <DocActionButton onClick={() => removeExtra(e.id)} title="Odebrat účastníka">
                        ✕
                      </DocActionButton>
                    }
                  />
                  <DocRow
                    k="Jméno"
                    value={e.jmeno}
                    onChange={(v) => updateExtra(e.id, { jmeno: v })}
                  />
                  <DocRow
                    k="Funkce"
                    value={e.funkce}
                    onChange={(v) => updateExtra(e.id, { funkce: v })}
                  />
                </Fragment>
              ))}
            </DocBlock>

            <DocSignatures
              items={[
                {
                  label: 'Zpracoval',
                  name: podpisy.zpracoval.jmeno || '—',
                  role: podpisy.zpracoval.funkce || '',
                  image: podpisy.zpracoval.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, zpracoval: { ...p.zpracoval, image: v } })),
                },
                {
                  label: 'Schválil',
                  name: podpisy.schvalil.jmeno || '—',
                  role: podpisy.schvalil.funkce || '',
                  image: podpisy.schvalil.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, schvalil: { ...p.schvalil, image: v } })),
                },
                ...podpisy.extra.map((e) => ({
                  label: e.strana || 'Účastník',
                  name: e.jmeno || '—',
                  role: e.funkce,
                  image: e.image,
                  onSign: (v: string) => updateExtra(e.id, { image: v }),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="nabidka"
        docTitle={`Cenová nabídka — ${obj.nazev || 'odběratel'}`}
        defaultTo={obj.email}
        defaultCc={dod.email}
        defaultSubject={docTitle}
        meta={`Odběratel: ${obj.nazev || '—'}${activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''}`}
      />
    </>
  );
}
